import { createFileRoute, redirect } from '@tanstack/react-router';

/**
 * Where partners apply: the programme's own page on Dub Partners, once it
 * exists. Until `VITE_PARTNER_PROGRAM_URL` is set the URL still goes to
 * `/agencies`, for the reasons below.
 */
const PARTNER_PROGRAM_URL = (() => {
  const value = import.meta.env.VITE_PARTNER_PROGRAM_URL;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
})();

/**
 * `/partners` → `/agencies`, permanently.
 *
 * ===========================================================================
 * Why this route exists and what it decides
 * ===========================================================================
 * The outgoing site serves `/partners` as "ReviewsJet White Label Program |
 * Join Our Partner Waitlist", offering agencies the chance to "resell under
 * your brand". The codebase implements none of that: there is no reseller
 * billing, no rebranding beyond hiding the widget mark on a paid plan, and no
 * partner platform. It is a waitlist for a product that was never built.
 *
 * The rebuild does not carry the page over, which left an indexed URL heading
 * for a 404 at launch — the one outcome the URL rules forbid outright. So it
 * redirects to `/agencies`, which serves the same audience with claims that are
 * true, including an explicit statement that white labelling does not exist.
 *
 * ===========================================================================
 * This is reversible, and it is somebody's call to reverse
 * ===========================================================================
 * A 301 is the right default: it beats a 404, it keeps the audience, and it
 * stops advertising something that does not exist. It also silently ends a
 * waitlist that may have been collecting addresses somebody intends to use.
 *
 * If the partner programme is still planned, the answer is to rebuild
 * `/partners` as an honest waitlist that says the programme has not launched —
 * and this file is what to delete when that happens. Deleting it without
 * building that page returns the URL to a 404.
 */
export const Route = createFileRoute('/partners')({
  loader: () => {
    if (PARTNER_PROGRAM_URL) {
      throw redirect({ href: PARTNER_PROGRAM_URL, statusCode: 302 });
    }
    throw redirect({ to: '/agencies', statusCode: 301 });
  },
});
